/** Reschedule and restore share the Visit-only appointment command and refresh the existing pane. */
import { computed, inject } from '@angular/core';
import { ActivityEntityName, JobSecurityRight, VisitBrowseMode, VisitBrowseRow, VisitContentSource } from '@jms/model';
import { VISIT_DATES_COMMAND, VisitDateOverride, VisitDatesRequest } from '@jms/model/entities/planned.work/visit/visit.dates';
import { RightsService } from '@jms/utils/util.permissions';
import { EntityDataService } from '@workbuddy/services/entities';
import { WorkBuddyDialogApi, WorkBuddyDialogService } from '@workbuddy/ui/v2/blocks/dialog';
import { ActionCommandCapability } from '@workbuddy/ui/v2/surface.engine';
import { VisitDatesDialogComponent } from '../dialogs/visit.dates/visit.dates.dialog';
import { confirmVisitWindow } from './visit.window.confirm';

export function visitDatesAction(restore: boolean, id: string, label: string): ActionCommandCapability<VisitBrowseRow> {

  const rights = inject(RightsService);
  const data = inject(EntityDataService);
  const dialogs = inject(WorkBuddyDialogService);
  const allowed = (row: VisitBrowseRow) => rights.hasRights(JobSecurityRight.Manage) && visitDatesEditable(row) && (!restore || row.datesOverridden === true);

  return {
    descriptor: ({ page }) => computed(() => ({
      id,
      label,
      iconLeft: restore ? 'fa-regular fa-arrow-rotate-left' : 'fa-regular fa-calendar',
      hidden: page.$readonly() || !allowed(page.$model()),
      disabled: page.$saving() || page.$dirty(),
      pin: 'never',
    })),
    run: async page => {
      const row = page.$model();
      if (page.$readonly() || page.$saving() || page.$dirty() || !allowed(row)) return;
      const request: VisitDatesRequest = { id: row._id, restore };
      if (!restore) {
        const dates = await openVisitDatesDialog(dialogs, row, label);
        if (!dates) return;
        const outside = await confirmVisitWindow(dialogs, row, dates.startDate);
        if (outside === null) return;
        request.dates = dates;
        if (outside) request.allowOutsideWindow = true;
      }
      const updated = await data.executeCommand<VisitBrowseRow>(ActivityEntityName.Visit, VISIT_DATES_COMMAND, request);
      if (!updated?._id) throw new Error('The date command did not return the updated Visit');
      page.reset({ ...updated });
      data.invalidateItems(ActivityEntityName.Visit);
      data.invalidateItems(ActivityEntityName.UpcomingWork);
    },
  };
}

/** A Job-less, live, stored-or-projected Planned attendance; a Job owns its own appointment. */
export function visitDatesEditable(row: VisitBrowseRow): boolean {

  return row.mode === VisitBrowseMode.Operational && !row.job?._id && row.active !== false
    && row.contentSource !== VisitContentSource.Snapshot && (!row.visitId || row.status === 'Planned');
}

export function openVisitDatesDialog(dialogs: WorkBuddyDialogApi, row: VisitBrowseRow, label: string, decision = false, approve = false): Promise<VisitDateOverride | undefined> {

  return dialogs.modal<VisitDateOverride, VisitDatesDialogComponent>(VisitDatesDialogComponent, {
    width: 'min(480px, calc(100vw - 40px))',
    align: 'top',
    aria: { label },
    inputs: { row, decision, approve },
  });
}

/**
 * Settles a passed appointment before construction: the prefilled current window confirms it, a new
 * one moves it. The server stamps the decision; construction refuses the passed date until it has one.
 * Resolves null when the person backs out, so nothing is written.
 */
export async function decideVisitAppointment(dialogs: WorkBuddyDialogApi, data: EntityDataService, row: VisitBrowseRow): Promise<(() => Promise<VisitBrowseRow>) | null> {

  const dates = await openVisitDatesDialog(dialogs, row, 'Confirm appointment', true);
  if (!dates) return null;
  const outside = await confirmVisitWindow(dialogs, row, dates.startDate);
  if (outside === null) return null;
  return async () => {
    const request: VisitDatesRequest = { id: row._id, restore: false, dates, ...(outside ? { allowOutsideWindow: true } : {}) };
    const updated = await data.executeCommand<VisitBrowseRow>(ActivityEntityName.Visit, VISIT_DATES_COMMAND, request);
    if (!updated?._id) throw new Error('The date command did not return the updated Visit');
    return updated;
  };
}
